import type { TLRecord } from '@digitalsamba/tldraw';
import type { AnyAction } from '@reduxjs/toolkit';
import { all, call, cancelled, delay, put, race, select, take } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import i18n from 'i18n';
import { formatErrorForLogging, logger } from 'utils/logger';
import { DRAFTS_LOG, DRAFTS_TAG } from 'utils/whiteboard/drafts/draftsLog';
import { SignalingSocket } from 'services/signaling';
import { notification } from 'features/notifications/toast/notification';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { selectRoomId } from 'features/room/roomSlice';
import { PermissionTypes } from 'features/permissions/types';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { hasPrivateWhiteboardAccessSaga } from 'features/whiteboard/sagas/hasPrivateWhiteboardAccessSaga';
import {
  gridPanelOpened,
  selectIsLockedContentReadOnly,
} from 'features/layout/features/content/contentSlice';
import { loaderDisplayChanged, whiteboardIdUpdated } from 'features/whiteboard/whiteboardSlice';
import { whiteboardDrafts } from 'utils/whiteboard/drafts/WhiteboardDraftStore';
import { WhiteboardDraft } from 'utils/whiteboard/drafts/types';
import { encodeWhiteboardRecord } from 'utils/whiteboard/encodeWhiteboardRecord';
import { WhiteboardRecordUpdate } from 'utils/whiteboard/settings';
import {
  draftRestoreFinished,
  draftRestoreStarted,
} from 'features/whiteboard/drafts/whiteboardDraftsSlice';
import { createBoardKeySaga } from 'features/whiteboard/drafts/sagas/createBoardKeySaga';
import { reseatRecordsSaga } from 'features/whiteboard/drafts/sagas/reseatRecordsSaga';

const RESTORE_TIMEOUT_MS = 15000;

export const restoredBoardOpened = (whiteboardId: string) => (action: AnyAction) =>
  whiteboardIdUpdated.match(action) && action.payload === whiteboardId;

export function* restoreWhiteboardSaga() {
  yield put(draftRestoreStarted());

  let started = false;
  let restored = false;

  // Filled in as the restore gets further, so a failure reports how far it got.
  const context: Record<string, unknown> = {};

  try {
    const draft: WhiteboardDraft | undefined = yield call(whiteboardDrafts.getDraft);

    if (!draft) {
      return;
    }

    const canEdit: boolean = yield call(hasPermissionsSaga, PermissionTypes.editWhiteboard);
    const hasPrivateAccess: boolean = yield call(hasPrivateWhiteboardAccessSaga, draft.isPrivate);
    const contentLocked: boolean = yield select(selectIsLockedContentReadOnly);

    if (!canEdit || !hasPrivateAccess || contentLocked) {
      return;
    }

    started = true;
    context.whiteboardId = draft.whiteboardId;
    context.isPrivate = draft.isPrivate;
    context.savedAt = draft.savedAt;

    yield put(gridPanelOpened('whiteboard'));
    yield put(loaderDisplayChanged(true));

    let records: TLRecord[] = yield call(whiteboardDrafts.readRecords);
    let encryptionKey: CryptoKey | undefined;

    const e2eeEnabled: boolean = yield select(selectE2eeEnabled);

    context.records = records.length;
    context.e2eeEnabled = e2eeEnabled;

    if (e2eeEnabled) {
      const roomId: string = yield select(selectRoomId);

      encryptionKey = yield call(createBoardKeySaga, draft.whiteboardId, roomId);
    }

    // Also unencrypted: the room may have dropped E2EE since the board was saved under it.
    records = yield call(reseatRecordsSaga, records, encryptionKey, draft.whiteboardId);
    context.recordsAfterReseat = records.length;

    const encoded: string[] = yield all(
      records.map((record) => call(encodeWhiteboardRecord, record, encryptionKey))
    );

    const shapes: WhiteboardRecordUpdate[] = records.map((record, index) => ({
      shapeId: record.id,
      data: encoded[index],
    }));

    const sent: boolean = yield call(SignalingSocket.send, {
      event: 'restoreWhiteboard',
      data: {
        id: draft.whiteboardId,
        name: draft.name,
        private: draft.isPrivate,
        shapes,
      },
    });

    if (!sent) {
      logger
        .remote(DRAFTS_LOG)
        .error('[drafts] Socket refused the restore, nothing was sent', context);

      return;
    }

    const { opened } = yield race({
      opened: take(restoredBoardOpened(draft.whiteboardId)),
      timeout: delay(RESTORE_TIMEOUT_MS),
    });

    if (!opened) {
      context.timedOutAfterMs = RESTORE_TIMEOUT_MS;

      throw new Error('Whiteboard restore went unanswered');
    }

    restored = true;
  } catch (error) {
    logger
      .remote(DRAFTS_LOG)
      .error('[drafts] Restore failed', { ...context, error: formatErrorForLogging(error) });

    yield call(Sentry.captureException, error, {
      tags: { feature: DRAFTS_TAG, operation: 'restore' },
      extra: context,
    });
  } finally {
    // A second click replaces this task, and the one that replaced it owns the loader now.
    const superseded: boolean = yield cancelled();

    if (started && !restored && !superseded) {
      yield put(loaderDisplayChanged(false));
      yield call(notification, i18n.t('notifications:wb_restore_failed'));
    }

    yield put(draftRestoreFinished());
  }
}
